import React, { useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion, useScroll, useTransform } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowRight, X, Plus, Minus, Check, ChevronLeft, ChevronRight } from 'lucide-react';
import { PRODUCTS, FAMILIES } from '../data/products';
import { Reveal } from '../components/Reveal';
import AtmoBg from '../components/AtmoBg';
import Breadcrumbs from '../components/Breadcrumbs';
import { productSchema, breadcrumbSchema } from '../lib/schema';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Ex-factory base prices for JSON-LD Product schema (matches HospitalityEstimator).
const BASE_PRICES = {
  cs37: 1990000, cs33: 1870000, cs31: 1750000, cs25: 1300000,
  ss35: 1970000, ss32: 1710000, ss26: 1370000,
};

const PRODUCT_FAQ = [
  { q: 'How long does production take?', a: 'Approximately 90 to 120 days from order confirmation to dispatch. On-site placement adds 1 to 3 days.' },
  { q: 'What customisation is possible?', a: 'Exterior paint, interior wood species, stone, textiles and select fittings can be tailored to your project.' },
  { q: 'What foundation is required?', a: 'A level, load-rated pad prepared to our specifications. This is the only significant civil work required at your site.' },
  { q: 'Is this warranted?', a: 'Yes — product-grade warranty: 10 years structural, 5 years envelope, 2 years MEP.' },
];

const CUSTOMISATION = [
  ['Exterior finish', 'Champagne · Onyx · Slate · Custom paint'],
  ['Interior wood',   'Oak · Walnut · Ash · Custom veneer'],
  ['Textiles',        'Wool · Linen · Bouclé · Custom fabric'],
  ['Stone & metal',   'Marble · Terrazzo · Brushed brass · Blackened steel'],
];

export default function ProductDetail() {
  const { slug } = useParams();
  const product = PRODUCTS.find((p) => p.slug === slug);
  const [lightbox, setLightbox] = useState(null);
  const [openFAQ, setOpenFAQ] = useState(0);
  const [enquiry, setEnquiry] = useState({ name: '', email: '', country: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const { scrollY } = useScroll();
  const parallax = useTransform(scrollY, [0, 900], [0, 200]);

  if (!product) return <Navigate to="/products" replace />;
  const family = FAMILIES[product.family];
  const related = PRODUCTS.filter((p) => p.family === product.family && p.slug !== product.slug).slice(0, 3);

  const submitEnquiry = async (e) => {
    e.preventDefault();
    if (submitting || sent) return;
    if (!enquiry.name || !enquiry.email || !enquiry.country || !enquiry.message) {
      toast.error('Please complete the required fields.'); return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/consultations`, {
        name: enquiry.name,
        email: enquiry.email,
        country: enquiry.country,
        phone: 'Not provided',
        project_type: `Enquiry: ${product.name}`,
        message: enquiry.message,
      });
      setSent(true);
      toast.success('Your enquiry has been received.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Something went wrong.');
    } finally { setSubmitting(false); }
  };

  return (
    <main data-testid={`product-detail-${slug}`} className="bg-white text-[#111]">
      <Helmet>
        <title>{`${product.name} | MODULAROOM`}</title>
        <meta name="description" content={`${product.name} is a ${family.name.toLowerCase()} by MODULAROOM. ${product.tagline} Factory-built luxury modular home for hospitality, residential and modular resort projects.`} />
        <link rel="canonical" href={`https://modularoom.com/products/${product.slug}`} />
        <script type="application/ld+json">
          {JSON.stringify(productSchema(product, family.name, BASE_PRICES[product.slug]))}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema([
            { name: 'Home', url: '/' },
            { name: 'Products', url: '/products' },
            { name: family.name, url: '/collections' },
            { name: product.name, url: `/products/${product.slug}` },
          ]))}
        </script>
      </Helmet>

      <section className="relative h-[100svh] w-full overflow-hidden bg-black text-white">
        <motion.div style={{ y: parallax }} className="absolute inset-0">
          <img src={product.hero} alt={`${product.name} luxury modular home by MODULAROOM · ${family.name}`} className="w-full h-full object-cover"/>
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/10 to-black/70"/>
        <div className="relative z-10 h-full max-w-[1440px] mx-auto px-6 md:px-12 flex flex-col justify-end pb-24 md:pb-32">
          <div className="text-[11px] tracking-[0.32em] uppercase text-[#C7A36A]">{family.name}</div>
          <h1 className="font-display text-6xl md:text-8xl lg:text-[10rem] mt-4 tracking-tight leading-none">{product.name}</h1>
          <div className="mt-6 text-lg md:text-xl text-white/85 max-w-xl font-light">{product.tagline}</div>
        </div>
      </section>

      {/* Breadcrumbs — appear below hero */}
      <div className="border-b border-black/5">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-5">
          <Breadcrumbs items={[
            {label:'Home', to:'/'},
            {label:'Products', to:'/products'},
            {label:family.name, to:'/collections'},
            {label:product.name},
          ]}/>
        </div>
      </div>

      <section className="relative overflow-hidden py-28 md:py-40">
        <AtmoBg src={product.hero} base="white" opacity={0.08} side="right" />
        <div className="relative max-w-[1440px] mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4 text-[11px] tracking-[0.32em] uppercase text-black/50">Overview</div>
          <div className="md:col-span-8">
            <Reveal>
              <p className="font-display text-3xl md:text-4xl leading-[1.15] tracking-tight">
                {product.overview}
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-[#F5F5F5] py-28 md:py-40">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <div className="text-[11px] tracking-[0.32em] uppercase text-black/50 mb-10">Specifications</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-10 border-t border-black/10 pt-10">
            {[
              ['Length', product.specs.length],
              ['Width', product.specs.width],
              ['Height', product.specs.height],
              ['Carpet Area', product.specs.carpetArea],
            ].map(([k, v]) => (
              <div key={k}>
                <div className="text-[11px] tracking-[0.28em] uppercase text-black/40">{k}</div>
                <div className="font-display text-3xl md:text-4xl mt-3 tracking-tight">{v}</div>
              </div>
            ))}
          </div>
          <div className="mt-16 grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-4 border-t border-black/10 pt-10">
            {product.features.map((f) => (
              <div key={f} className="text-sm tracking-[0.14em] uppercase text-black/70">· {f}</div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ ENGINEERING & ASSURANCE ============ */}
      <section className="relative bg-[#111] text-white py-32 md:py-48 overflow-hidden">
        <div className="absolute inset-0 blueprint-grid-dark pointer-events-none" />
        <div className="relative max-w-[1440px] mx-auto px-6 md:px-12">
          <div className="flex items-center gap-4 text-[11px] tracking-[0.32em] uppercase text-white/55">
            <span className="text-[#C7A36A]">/ 02</span>
            <span className="w-10 border-t border-white/25" />
            <span>Engineering & Assurance</span>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
            <h2 className="md:col-span-8 font-display text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.0]">
              Built to product-grade tolerances.<br/>
              <span className="italic text-white/40">Delivered to hospitality standards.</span>
            </h2>
            <p className="md:col-span-4 text-sm md:text-[15px] text-white/60 leading-relaxed md:justify-self-end max-w-sm">
              Every {product.name} is engineered as a signature product with the discipline of automotive manufacturing and the finish of luxury hospitality.
            </p>
          </div>

          {/* 6 blueprint cards */}
          <div className="mt-20 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
            {[
              {
                id: '01', title: 'Structural',
                lead: 'Corrosion-protected steel frame engineered for long-term performance.',
                specs: [['Tolerance', '±2 mm'], ['Frame', 'Steel · powder-coated'], ['Rating', 'Wind · seismic ready']],
              },
              {
                id: '02', title: 'Envelope',
                lead: 'A finished shell, sealed, painted and glazed inside the factory.',
                specs: [['Cladding', 'Aluminium composite'], ['Finish', '2K automotive paint'], ['Seal', 'Waterproof · rated']],
              },
              {
                id: '03', title: 'Thermal & Acoustic',
                lead: 'High-density insulation and insulated glazing hold comfort year-round.',
                specs: [['Insulation', 'PU · high density'], ['Glazing', 'Double · low-E'], ['Envelope', 'Continuous seal']],
              },
              {
                id: '04', title: 'Transport',
                lead: 'Delivered as a single, factory-finished module. Roadworthy anywhere.',
                specs: [['Mode', 'Road · sea'], ['Envelope', product.specs.length + ' × ' + product.specs.width], ['Reach', 'Worldwide']],
              },
              {
                id: '05', title: 'Installation',
                lead: 'Placed, levelled and connected on-site. No on-site construction.',
                specs: [['Timeline', '1–3 days'], ['Site work', 'Foundation · utilities'], ['Handover', 'Turnkey']],
              },
              {
                id: '06', title: 'Assurance',
                lead: 'Product-grade warranty across structural, envelope and MEP systems.',
                specs: [['Structural', '10 yr'], ['Envelope', '5 yr'], ['Roadmap', 'ISO · CE (2027)']],
              },
            ].map((c) => (
              <div key={c.id} className="bg-[#111] p-8 md:p-10 min-h-[280px] flex flex-col">
                <div className="flex items-center justify-between">
                  <div className="text-[10px] tracking-[0.32em] uppercase text-[#C7A36A]">{c.id}</div>
                  <div className="font-mono-luxe text-[11px] text-white/40">— {c.title.toUpperCase()}</div>
                </div>
                <div className="mt-6 font-display text-2xl md:text-3xl tracking-tight">{c.title}</div>
                <div className="mt-3 text-sm text-white/60 leading-relaxed max-w-xs">{c.lead}</div>
                <div className="mt-auto pt-8 border-t border-white/10 grid grid-cols-1 gap-1.5 font-mono-luxe text-[12px] text-white/85">
                  {c.specs.map(([k, v]) => (
                    <div key={k} className="flex justify-between">
                      <span className="text-white/40">{k}</span>
                      <span>{v}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Compliance roadmap strip */}
          <div className="mt-20 border-t border-white/10 pt-10 grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="md:col-span-1 text-[10px] tracking-[0.32em] uppercase text-white/50">Future Roadmap</div>
            <div className="md:col-span-3 grid grid-cols-1 sm:grid-cols-3 gap-8">
              {[
                ['2026', 'IS-code alignment · in-house QA'],
                ['2027', 'ISO 9001 · CE marking pathway'],
                ['2028', 'Third-party certifications · global markets'],
              ].map(([y, n]) => (
                <div key={y}>
                  <div className="font-mono-luxe text-[#C7A36A] text-lg">{y}</div>
                  <div className="mt-2 text-sm text-white/70 leading-relaxed">{n}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============ INTERIOR GALLERY — Editorial Mosaic ============ */}
      <ProductGallery images={product.gallery} name={product.name} onOpen={setLightbox} />

      {/* CUSTOMIZATION */}
      <section className="bg-white text-[#111] py-28 md:py-40">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-14 items-start">
          <div className="md:col-span-5">
            <div className="text-[10px] tracking-[0.32em] uppercase text-[#C7A36A]">Customisation</div>
            <h2 className="mt-4 font-display text-4xl md:text-5xl tracking-tight leading-[1.02]">Configure {product.name}<br/><span className="italic text-black/40">to your site.</span></h2>
            <p className="mt-6 text-black/65 leading-relaxed max-w-md">Every {product.name} is bespoke-finished. Choose from a curated palette or specify a custom material with your architect.</p>
          </div>
          <div className="md:col-span-7 grid grid-cols-1 gap-y-6">
            {CUSTOMISATION.map(([k, v]) => (
              <div key={k} className="border-t border-black/10 pt-5 grid grid-cols-12 gap-6">
                <div className="col-span-4 md:col-span-3 text-[11px] tracking-[0.28em] uppercase text-black/45">{k}</div>
                <div className="col-span-8 md:col-span-9 font-display text-lg md:text-xl tracking-tight">{v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-[#F5F5F5] text-[#111] py-28 md:py-40">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-14">
          <div className="md:col-span-4">
            <div className="text-[10px] tracking-[0.32em] uppercase text-[#C7A36A]">FAQ</div>
            <h2 className="mt-4 font-display text-4xl md:text-5xl tracking-tight leading-[1.02]">Common questions<br/><span className="italic text-black/40">about {product.name}.</span></h2>
            <Link to="/faq" className="mt-6 inline-flex items-center gap-2 text-[12px] tracking-[0.22em] uppercase hover:text-[#C7A36A] transition-colors">All FAQs →</Link>
          </div>
          <div className="md:col-span-8">
            {PRODUCT_FAQ.map((f, i) => (
              <div key={i} className="border-t border-black/10">
                <button onClick={() => setOpenFAQ(openFAQ === i ? -1 : i)} className="w-full py-5 flex items-start justify-between gap-6 text-left">
                  <div className="font-display text-lg md:text-xl tracking-tight max-w-2xl">{f.q}</div>
                  <div className="mt-1 text-black/50 shrink-0">{openFAQ === i ? <Minus size={16}/> : <Plus size={16}/>}</div>
                </button>
                <div className={`overflow-hidden transition-all duration-500 ${openFAQ === i ? 'max-h-64 pb-6' : 'max-h-0'}`}>
                  <div className="max-w-2xl text-black/65 leading-relaxed text-sm">{f.a}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ENQUIRY MINI-FORM */}
      <section className="bg-[#111] text-white py-28 md:py-40">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-14 items-start">
          <div className="md:col-span-5">
            <div className="text-[10px] tracking-[0.32em] uppercase text-[#C7A36A]">Enquire about {product.name}</div>
            <h2 className="mt-4 font-display text-4xl md:text-5xl tracking-tight leading-[1.02]">Speak with our team.</h2>
            <p className="mt-6 max-w-md text-white/70 leading-relaxed">Tell us about your site and timeline. A MODULAROOM specialist will respond within one business day.</p>
          </div>
          <div className="md:col-span-7">
            {sent ? (
              <div data-testid="enquiry-success" className="rounded-2xl border border-white/10 bg-white/[0.03] p-8 flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-[#C7A36A] text-[#111] flex items-center justify-center shrink-0"><Check size={18}/></div>
                <div>
                  <div className="font-display text-2xl tracking-tight">Enquiry received.</div>
                  <div className="mt-2 text-white/70 text-sm max-w-md">Our team will contact you shortly to continue the conversation about the {product.name}.</div>
                </div>
              </div>
            ) : (
              <form onSubmit={submitEnquiry} data-testid="enquiry-form" className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                <div>
                  <label className="text-[10px] tracking-[0.28em] uppercase text-white/50">Name *</label>
                  <input data-testid="enq-name" className="editorial-input" value={enquiry.name} onChange={(e) => setEnquiry(f => ({...f, name: e.target.value}))} placeholder="Your full name"/>
                </div>
                <div>
                  <label className="text-[10px] tracking-[0.28em] uppercase text-white/50">Email *</label>
                  <input data-testid="enq-email" type="email" className="editorial-input" value={enquiry.email} onChange={(e) => setEnquiry(f => ({...f, email: e.target.value}))} placeholder="you@company.com"/>
                </div>
                <div className="md:col-span-2">
                  <label className="text-[10px] tracking-[0.28em] uppercase text-white/50">Country *</label>
                  <input data-testid="enq-country" className="editorial-input" value={enquiry.country} onChange={(e) => setEnquiry(f => ({...f, country: e.target.value}))} placeholder="India / UAE / …"/>
                </div>
                <div className="md:col-span-2">
                  <label className="text-[10px] tracking-[0.28em] uppercase text-white/50">Message *</label>
                  <textarea data-testid="enq-message" rows={3} className="editorial-input" value={enquiry.message} onChange={(e) => setEnquiry(f => ({...f, message: e.target.value}))} placeholder={`Tell us about your project involving ${product.name}.`}/>
                </div>
                <div className="md:col-span-2 mt-3">
                  <button data-testid="enq-submit" disabled={submitting} className="inline-flex items-center gap-2 rounded-full bg-white text-[#111] px-7 py-3.5 text-[13px] tracking-[0.2em] uppercase hover:bg-[#C7A36A] hover:text-white transition-colors disabled:opacity-60">
                    {submitting ? 'Sending…' : `Enquire about ${product.name}`} <ArrowRight size={14}/>
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* RELATED MODELS */}
      {related.length > 0 && (
        <section className="bg-white text-[#111] py-28 md:py-40">
          <div className="max-w-[1440px] mx-auto px-6 md:px-12">
            <div className="flex items-end justify-between mb-12">
              <div>
                <div className="text-[10px] tracking-[0.32em] uppercase text-[#C7A36A]">Also in {family.name}</div>
                <h2 className="mt-4 font-display text-3xl md:text-4xl tracking-tight leading-[1.02]">More models to consider.</h2>
              </div>
              <Link to="/collections" className="text-[12px] tracking-[0.22em] uppercase hover:text-[#C7A36A] transition-colors">View collection →</Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {related.map((r) => (
                <Link key={r.slug} to={`/products/${r.slug}`} className="group block">
                  <div className="relative aspect-[4/5] overflow-hidden bg-[#F5F5F5]">
                    <img src={r.hero} alt={r.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1500ms] ease-out group-hover:scale-[1.045]"/>
                  </div>
                  <div className="mt-5 flex items-baseline justify-between">
                    <div className="font-display text-3xl tracking-tight">{r.name}</div>
                    <div className="font-mono-luxe text-[11px] text-black/60">{r.specs.carpetArea}</div>
                  </div>
                  <div className="mt-2 text-sm text-black/55">{r.tagline}</div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {lightbox && (
        <Lightbox
          images={product.gallery}
          index={product.gallery.indexOf(lightbox)}
          onClose={() => setLightbox(null)}
          onChange={(i) => setLightbox(product.gallery[i])}
        />
      )}
    </main>
  );
}

// ---------- Interior Gallery — Editorial Mosaic ----------
// Adapts to any image count with three intelligent layouts:
//   1. Full-bleed cinematic hero (first image)
//   2. Alternating asymmetric pairs (55/45 → 45/55) for remaining images
//   3. Wide 21:9 cinematic strip for any single leftover image
// No empty white space regardless of count (3, 4, 7, 8, 9…).
const GalleryTile = ({ src, idx, name, caption, aspect, onOpen }) => (
  <button
    onClick={() => onOpen(src)}
    data-testid={`gallery-${idx}`}
    className={`group relative w-full overflow-hidden bg-[#F5F5F5] ${aspect}`}
  >
    <img
      src={src}
      alt={`${name} interior view ${idx + 1}`}
      loading="lazy"
      className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1600ms] ease-out group-hover:scale-[1.05]"
    />
    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
    <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between text-white opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500">
      <div className="text-[10px] tracking-[0.32em] uppercase text-white/80">{caption}</div>
      <div className="text-[10px] tracking-[0.22em] uppercase text-[#C7A36A]">View</div>
    </div>
    <div className="absolute top-5 left-5 text-[10px] tracking-[0.28em] uppercase text-white/85 mix-blend-difference">
      /{String(idx + 1).padStart(2, '0')}
    </div>
  </button>
);

function ProductGallery({ images, name, onOpen }) {
  if (!images || images.length === 0) return null;
  const [hero, ...rest] = images;

  // Group the remaining images into pairs; single leftover stays alone.
  const groups = [];
  for (let i = 0; i < rest.length; i += 2) {
    groups.push(rest.slice(i, i + 2));
  }

  return (
    <section className="py-24 md:py-32" data-testid="interior-gallery">
      {/* Header */}
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 mb-10 md:mb-14">
        <div className="flex items-end justify-between gap-4">
          <div>
            <div className="text-[10px] tracking-[0.32em] uppercase text-[#C7A36A]">Interior Gallery</div>
            <h2 className="mt-4 font-display text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.02]">
              Inside the {name}.
            </h2>
          </div>
          <div className="hidden md:block text-[11px] tracking-[0.22em] uppercase text-black/45">
            {images.length} views · click to expand
          </div>
        </div>
      </div>

      {/* Full-bleed cinematic hero */}
      <div className="w-full">
        <button
          onClick={() => onOpen(hero)}
          data-testid="gallery-0"
          className="group relative block w-full overflow-hidden bg-[#F5F5F5] h-[70svh] md:h-[85svh]"
        >
          <img
            src={hero}
            alt={`${name} interior — hero view`}
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-[2000ms] ease-out group-hover:scale-[1.04]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-70" />
          <div className="absolute bottom-8 left-6 md:bottom-12 md:left-12 right-6 md:right-12 flex items-end justify-between text-white">
            <div>
              <div className="text-[10px] tracking-[0.32em] uppercase text-[#C7A36A]">Interior</div>
              <div className="mt-3 font-display text-3xl md:text-5xl lg:text-6xl tracking-tight leading-[1.0]">{name}</div>
            </div>
            <div className="hidden md:flex items-center gap-3 text-[11px] tracking-[0.22em] uppercase text-white/70">
              <span className="w-10 border-t border-white/40" />
              <span>01</span>
            </div>
          </div>
        </button>
      </div>

      {/* Asymmetric pairs — alternate 55/45 and 45/55 */}
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 mt-4 md:mt-6 space-y-4 md:space-y-6">
        {groups.map((g, gi) => {
          const idxBase = 1 + gi * 2;
          if (g.length === 2) {
            const flip = gi % 2 === 1;
            return (
              <div key={gi} className="grid grid-cols-1 md:grid-cols-11 gap-4 md:gap-6">
                <div className={`${flip ? 'md:col-span-5 md:order-2' : 'md:col-span-6'}`}>
                  <GalleryTile src={g[0]} idx={idxBase} name={name} caption="Interior detail" aspect={flip ? 'aspect-[4/5]' : 'aspect-[5/6]'} onOpen={onOpen} />
                </div>
                <div className={`${flip ? 'md:col-span-6 md:order-1' : 'md:col-span-5'}`}>
                  <GalleryTile src={g[1]} idx={idxBase + 1} name={name} caption="Interior detail" aspect={flip ? 'aspect-[5/6]' : 'aspect-[4/5]'} onOpen={onOpen} />
                </div>
              </div>
            );
          }
          // Single leftover — full-bleed cinematic strip
          return (
            <div key={gi} className="-mx-6 md:-mx-12">
              <button
                onClick={() => onOpen(g[0])}
                data-testid={`gallery-${idxBase}`}
                className="group relative block w-full overflow-hidden bg-[#F5F5F5] aspect-[21/9]"
              >
                <img
                  src={g[0]}
                  alt={`${name} interior detail ${idxBase + 1}`}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1800ms] ease-out group-hover:scale-[1.04]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
                <div className="absolute bottom-6 left-6 md:bottom-8 md:left-12 flex items-end gap-4 text-white opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-500">
                  <div className="text-[10px] tracking-[0.32em] uppercase text-[#C7A36A]">Interior</div>
                  <div className="text-[10px] tracking-[0.22em] uppercase text-white/75">/{String(idxBase + 1).padStart(2, '0')}</div>
                </div>
              </button>
            </div>
          );
        })}
      </div>
    </section>
  );
}

// ---------- Lightbox with prev/next + keyboard nav ----------
function Lightbox({ images, index, onClose, onChange }) {
  const prev = React.useCallback(() => onChange((index - 1 + images.length) % images.length), [index, images.length, onChange]);
  const next = React.useCallback(() => onChange((index + 1) % images.length), [index, images.length, onChange]);

  React.useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft') prev();
      else if (e.key === 'ArrowRight') next();
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [prev, next, onClose]);

  const current = images[index];
  return (
    <div className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4 md:p-10" onClick={onClose} data-testid="lightbox">
      <button data-testid="lightbox-close" className="absolute top-6 right-6 text-white/80 hover:text-white transition-colors" onClick={onClose} aria-label="Close">
        <X size={26} />
      </button>
      {images.length > 1 && (
        <>
          <button
            onClick={(e) => { e.stopPropagation(); prev(); }}
            data-testid="lightbox-prev"
            aria-label="Previous image"
            className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full border border-white/25 text-white/80 hover:bg-white hover:text-[#111] hover:border-white transition-colors flex items-center justify-center"
          ><ChevronLeft size={22} /></button>
          <button
            onClick={(e) => { e.stopPropagation(); next(); }}
            data-testid="lightbox-next"
            aria-label="Next image"
            className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full border border-white/25 text-white/80 hover:bg-white hover:text-[#111] hover:border-white transition-colors flex items-center justify-center"
          ><ChevronRight size={22} /></button>
        </>
      )}
      <img
        src={current}
        alt={`Preview ${index + 1} of ${images.length}`}
        onClick={(e) => e.stopPropagation()}
        className="max-h-[88vh] max-w-[92vw] object-contain"
      />
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-[11px] tracking-[0.32em] uppercase text-white/60">
        {String(index + 1).padStart(2, '0')} / {String(images.length).padStart(2, '0')}
      </div>
    </div>
  );
}
